import { useNode, useEditor } from "@craftjs/core";
import React, { useContext, useEffect } from "react";
import {
  ColorInput,
  RangeInput,
  SelectInput,
  UnitInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  Spacer,
  AlignInput,
  RichTextFormatting,
  ToggleInput,
  RangeNumberInput,
  TextInput,
  NumberInput,
  TabsV2,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import {
  changeTransition,
  isUpdatedFromDefault,
  getDirectoryInfoFromParents,
  getDirectoryFieldOptions,
} from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { remove_prefix } from "../../helpers";
import { GrigoraEditorContext } from "../../editor/context";

export const TextSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
  } = useEditor();

  const parents = node(id).ancestors(true);

  const { directoryTypes } = useContext(GrigoraDashboardContext) ?? {};
  const { pagesData, currentPage } = useContext(GrigoraEditorContext) ?? {};

  // Effect to auto-select directory source when appropriate
  useEffect(() => {
    if (props.isContentDynamic && !props.dynamicContentSrc) {
      const directoryInfo = getDirectoryInfoFromParents(parents, node);
      if (directoryInfo) {
        setProp((props) => {
          props.dynamicContentSrc = "directory-listing";
          props.dynamicContentSrcData = {
            directory_type_id: directoryInfo.directoryTypeId,
            directory_field: "name",
          };
        });
      } else {
        // Check if current page is a directory listing page
        var currentPageData = pagesData?.[currentPage] ?? {};

        if (
          currentPageData?.type === "template" &&
          currentPageData?.template === "directory-template" &&
          currentPageData?.directory_id
        ) {
          var pageDirectoryId = currentPageData?.directory_id;

          var directoryType = directoryTypes?.find(
            (type) =>
              remove_prefix(type.sk, "directory-type-") === pageDirectoryId,
          );

          if (directoryType) {
            setProp((props) => {
              props.dynamicContentSrc = "directory-listing";
              props.dynamicContentSrcData = {
                directory_type_id: pageDirectoryId,
                directory_field: "name",
              };
            });
          }
        }
      }
    }
  }, [props.isContentDynamic]);

  const {
    isContentDynamic,
    dynamicContentSrc,
    dynamicContentSrcData,
    structureTag = "p",
    layoutColumns,
    layoutColumnsGap,
    align,
    alignMobile,
    alignTablet,
    textColor,
    hoverTextColor,
    backgroundColor,
    hoverBackgroundColor,
    typoSize,
    typoSizeTablet,
    typoSizeMobile,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    hoverLayoutPadding,
    hoverLayoutPaddingTablet,
    hoverLayoutPaddingMobile,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    hoverLayoutMargin,
    hoverLayoutMarginTablet,
    hoverLayoutMarginMobile,
    hasMaxWordsLimit,
    maxTextLength = 55,
    textSuffix = "",
    suffixOnMaxWordsReached,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            resetValue={"p"}
            value={structureTag}
            onChange={(val) => {
              setProp((props) => {
                props.structureTag = val;
              });
            }}
            options={[
              {
                label: "Paragraph",
                value: "p",
              },
              {
                label: "Heading 1",
                value: "h1",
              },
              {
                label: "Heading 2",
                value: "h2",
              },
              {
                label: "Heading 3",
                value: "h3",
              },
              {
                label: "Heading 4",
                value: "h4",
              },
              {
                label: "Heading 5",
                value: "h5",
              },
              {
                label: "Heading 6",
                value: "h6",
              },
              {
                label: "Span",
                value: "span",
              },
            ]}
            label={"Tag"}
          />

          <AlignInput
            value={align}
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <ToggleInput
            isResetVisible={false}
            label="Is Text Dynamic?"
            value={isContentDynamic}
            onChange={(val) => {
              setProp((props) => {
                props.isContentDynamic = val;
                props.dynamicContentSrc = undefined;
                props.hasMaxWordsLimit = undefined;
                props.maxTextLength = undefined;
                props.textSuffix = undefined;
                props.suffixOnMaxWordsReached = undefined;
              });
            }}
          />
          {isContentDynamic ? (
            <>
              <SelectInput
                isResetVisible={false}
                label="Source"
                value={dynamicContentSrc}
                onChange={(val) => {
                  setProp((props) => {
                    props.dynamicContentSrc = val;
                  });
                }}
                options={[
                  {
                    label: "-",
                    value: "",
                  },
                  {
                    label: "Post Title",
                    value: "post-title",
                  },
                  {
                    label: "Post Summary",
                    value: "post-excerpt",
                  },
                  {
                    label: "Category Name",
                    value: "category-name",
                  },
                  {
                    label: "Category Description",
                    value: "category-description",
                  },
                  {
                    label: "Directory Listing",
                    value: "directory-listing",
                  },
                ]}
              />
              {dynamicContentSrc === "directory-listing" &&
                (directoryTypes?.length > 0 ? (
                  <>
                    <SelectInput
                      isResetVisible={false}
                      label="Directory"
                      value={dynamicContentSrcData?.directory_type_id}
                      onChange={(val) => {
                        setProp((props) => {
                          props.dynamicContentSrcData = {
                            ...(props.dynamicContentSrcData ?? {}),
                            directory_type_id: val,
                          };
                        });
                      }}
                      options={[
                        {
                          label: "Select Directory",
                          value: "",
                        },
                        ...directoryTypes?.map((type) => ({
                          label: type.name,
                          value: remove_prefix(type.sk, "directory-type-"),
                        })),
                      ]}
                    />
                    {dynamicContentSrcData?.directory_type_id && (
                      <SelectInput
                        isResetVisible={false}
                        label="Directory Field"
                        value={dynamicContentSrcData?.directory_field}
                        onChange={(val) => {
                          setProp((props) => {
                            props.dynamicContentSrcData = {
                              ...(props.dynamicContentSrcData ?? {}),
                              directory_field: val,
                            };
                          });
                        }}
                        options={getDirectoryFieldOptions(
                          directoryTypes,
                          dynamicContentSrcData?.directory_type_id,
                          ["text", "richtext", "datetime", "option"],
                        )}
                      />
                    )}
                    {dynamicContentSrcData?.directory_field &&
                      directoryTypes
                        ?.find(
                          (type) =>
                            remove_prefix(type.sk, "directory-type-") ===
                            dynamicContentSrcData?.directory_type_id,
                        )
                        ?.fields?.find((field) => {
                          // Handle both direct fields and reference fields
                          if (
                            dynamicContentSrcData.directory_field.includes(".")
                          ) {
                            const [refFieldId, subFieldId] =
                              dynamicContentSrcData.directory_field.split(".");
                            const refField =
                              field.id === refFieldId &&
                              field.type === "reference";
                            if (refField) {
                              const referencedType = directoryTypes?.find(
                                (type) =>
                                  remove_prefix(type.sk, "directory-type-") ===
                                  field.referenceTypeId,
                              );
                              const subField = referencedType?.fields?.find(
                                (f) => f.id === subFieldId,
                              );
                              return subField?.type === "datetime";
                            }
                            return false;
                          }
                          return (
                            field.id ===
                              dynamicContentSrcData.directory_field &&
                            field.type === "datetime"
                          );
                        }) && (
                        <SelectInput
                          isResetVisible={false}
                          label="Date Format"
                          value={dynamicContentSrcData?.dateFormat}
                          onChange={(val) => {
                            setProp((props) => {
                              props.dynamicContentSrcData = {
                                ...(props.dynamicContentSrcData ?? {}),
                                dateFormat: val,
                              };
                            });
                          }}
                          options={[
                            {
                              label: "Select Format",
                              value: "",
                            },
                            {
                              label: "MM/DD/YYYY",
                              value: "mm/dd/yyyy",
                            },
                            {
                              label: "DD/MM/YYYY",
                              value: "dd/mm/yyyy",
                            },
                            {
                              label: "YYYY/MM/DD",
                              value: "yyyy/mm/dd",
                            },
                            {
                              label: "Month DD, YYYY",
                              value: "mmmm d, yyyy",
                            },
                            {
                              label: "DD Month, YYYY",
                              value: "d mmmm, yyyy",
                            },
                            {
                              label: "Mon DD, YYYY",
                              value: "mmm d, yyyy",
                            },
                            {
                              label: "DD Mon, YYYY",
                              value: "d mmm, yyyy",
                            },
                            {
                              label: "ISO Date",
                              value: "isoDate",
                            },
                            {
                              label: "Full Date",
                              value: "fullDate",
                            },
                          ]}
                        />
                      )}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      No directory types found
                    </span>
                  </>
                ))}
              <ToggleInput
                isResetVisible={false}
                label="Limit Words"
                value={hasMaxWordsLimit}
                onChange={(val) => {
                  setProp((props) => {
                    props.hasMaxWordsLimit = val;
                  });
                }}
              />
              {hasMaxWordsLimit && (
                <RangeNumberInput
                  resetValue={undefined}
                  value={maxTextLength}
                  onChange={(val) => {
                    setProp((props) => {
                      props.maxTextLength = val;
                    });
                  }}
                  min={1}
                  max={100}
                  step={1}
                  label={"Max Words"}
                />
              )}
              <TextInput
                minLength={INPUT_LIMITS["text-suffix"].minlength}
                maxLength={INPUT_LIMITS["text-suffix"].maxlength}
                resetValue={""}
                label="Suffix"
                value={textSuffix}
                onChange={(val) => {
                  setProp((props) => {
                    props.textSuffix = val;
                  });
                }}
              />
              {textSuffix && (
                <ToggleInput
                  isResetVisible={false}
                  label="Only Show Suffix When Max Words Are Reached"
                  value={suffixOnMaxWordsReached}
                  onChange={(val) => {
                    setProp((props) => {
                      props.suffixOnMaxWordsReached = val;
                    });
                  }}
                />
              )}
            </>
          ) : (
            <></>
          )}
        </Spacer>
        <Panel
          title={"Typography"}
          initialOpen={false}
          id={`block-setting-${id}-typography`}
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoSizeTablet },
            { key: typoSizeMobile },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoTransform },
            { key: typoStyle },
            { key: typoWeight },
          ])}
        >
          <UnitInput
            value={typoSize}
            isResponsive={true}
            valueTablet={typoSizeTablet}
            valueMobile={typoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.typoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.typoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <RichTextFormatting />
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalColorSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverColorSettings(),
      },
    ];

    function normalColorSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={textColor}
            onChange={(val) => {
              setProp((props) => {
                props.textColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={backgroundColor}
            onChange={(val) => {
              setProp((props) => {
                props.backgroundColor = val;
              });
            }}
            label={"Background"}
          />
        </>
      );
    }

    function hoverColorSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={hoverTextColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverTextColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            resetValue={undefined}
            value={hoverBackgroundColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBackgroundColor = val;
              });
            }}
            label={"Background"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["color"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["color", "background-color"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <>
        <Panel
          id={`block-setting-${id}-colors`}
          title={"Colors"}
          initialOpen={true}
          isUpdated={isUpdatedFromDefault([
            { key: textColor },
            { key: backgroundColor },
            { key: hoverTextColor },
            { key: hoverBackgroundColor },
          ])}
        >
          <TabsV2 items={colorTabs} />
        </Panel>
      </>
    );
  }

  function AdvancedSettings() {
    const margnPaddingTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverSettings(),
      },
    ];

    function normalSettings() {
      return (
        <>
          {" "}
          <MarginInput
            isResponsive={true}
            value={layoutMargin}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            value={layoutPadding}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </>
      );
    }

    function hoverSettings() {
      return (
        <>
          {" "}
          <MarginInput
            isResponsive={true}
            value={hoverLayoutMargin}
            valueTablet={hoverLayoutMarginTablet}
            valueMobile={hoverLayoutMarginMobile}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLayoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverLayoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverLayoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            value={hoverLayoutPadding}
            valueTablet={hoverLayoutPaddingTablet}
            valueMobile={hoverLayoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.hoverLayoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.hoverLayoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.hoverLayoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("margin", "padding")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["margin", "padding"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <>
        <Panel
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutPadding },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
            { key: hoverLayoutMargin },
            { key: hoverLayoutMarginTablet },
            { key: hoverLayoutMarginMobile },
            { key: hoverLayoutPadding },
            { key: hoverLayoutPaddingTablet },
            { key: hoverLayoutPaddingMobile },
          ])}
          initialOpen={false}
        >
          <TabsV2 items={margnPaddingTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutColumns },
            { key: layoutColumnsGap },
          ])}
          id={`block-setting-${id}-columns`}
          title={"Columns"}
          initialOpen={false}
        >
          <RangeNumberInput
            resetValue={undefined}
            value={layoutColumns}
            onChange={(val) => {
              setProp((props) => {
                props.layoutColumns = val;
              });
            }}
            min={0}
            max={20}
            label={"Columns"}
          />
          <UnitInput
            resetValue={undefined}
            value={layoutColumnsGap}
            onChange={(val) => {
              setProp((props) => {
                props.layoutColumnsGap = val;
              });
            }}
            label={"Columns Gap"}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: structureTag, default: "p" },
        { key: align },
        { key: alignTablet },
        { key: alignMobile },
        { key: isContentDynamic },
        { key: dynamicContentSrc },
        { key: hasMaxWordsLimit },
        { key: maxTextLength, default: 55 },
        { key: textSuffix },
        { key: suffixOnMaxWordsReached },
        { key: typoSize },
        { key: typoSizeTablet },
        { key: typoSizeMobile },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoTransform },
        { key: typoStyle },
        { key: typoWeight },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: hoverTextColor },
        { key: hoverBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: layoutColumns },
        { key: layoutColumnsGap },
        { key: hoverLayoutMargin },
        { key: hoverLayoutMarginTablet },
        { key: hoverLayoutMarginMobile },
        { key: hoverLayoutPadding },
        { key: hoverLayoutPaddingTablet },
        { key: hoverLayoutPaddingMobile },
      ]),
    },
  ];

  return (
    <div>
      <Tabs type="block-setting" id={`block-settings-tab-${id}`} items={tabs} />
    </div>
  );
};
